import type { ReactNode } from 'react'
import { useState } from 'react'
import { AlertCircle, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/cn'

export interface FormSection {
  key: string
  label: string
  icon?: LucideIcon
  content: ReactNode
  /** Quando true mostra alerta vermelho no item do sidemenu. */
  hasError?: boolean
  /** Quantidade de erros — exibe bolha numérica ao lado do alerta. */
  errorCount?: number
  /** Esconde a section (útil para passos só visíveis em edição). */
  hidden?: boolean
  /** Quantidade de registros da seção, mostrada à direita do rótulo. */
  count?: number
}

interface Props {
  sections: FormSection[]
  /** Section key inicial. Default = primeira não-oculta. */
  initialKey?: string
  /**
   * Quando definido, o componente é controlado: o consumidor decide qual
   * section mostrar (útil para ativar a com erro após submit).
   */
  activeKey?: string
  onChange?: (key: string) => void
  className?: string
}

/**
 * Layout padrão para formulários com seções (regra global — substitui cards
 * separadores).
 *
 *  - Desktop (≥md): sidebar 240px fixa à esquerda + conteúdo à direita
 *  - Mobile (<md): lista de chips horizontal scrollable no topo
 *  - Item ativo: barra azul à esquerda + bg suave
 *  - Item com erro: ícone alerta vermelho + (opcional) badge numérica
 *
 * O componente NÃO valida — só EXIBE o estado de erro vindo via `hasError`.
 */
export function FormSidemenu({ sections, initialKey, activeKey, onChange, className }: Props) {
  const visible = sections.filter((s) => !s.hidden)
  const [internal, setInternal] = useState<string>(initialKey ?? visible[0]?.key ?? '')
  const current = activeKey ?? internal
  function select(key: string) {
    if (activeKey === undefined) setInternal(key)
    onChange?.(key)
  }
  const active = visible.find((s) => s.key === current) ?? visible[0]

  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4 md:gap-6', className)}>
      {/* Sidemenu */}
      <aside className="md:sticky md:top-4 md:self-start">
        {/* Mobile: tabs horizontais */}
        <nav className="md:hidden -mx-1 px-1 overflow-x-auto">
          <ul className="flex gap-2 min-w-max">
            {visible.map((s) => (
              <li key={s.key}>
                <SideItem
                  section={s}
                  active={s.key === current}
                  onClick={() => select(s.key)}
                  variant="chip"
                />
              </li>
            ))}
          </ul>
        </nav>
        {/* Desktop: vertical */}
        <nav className="hidden md:block bg-white rounded-md ring-1 ring-gray-200 p-2">
          <ul className="flex flex-col gap-1">
            {visible.map((s) => (
              <li key={s.key}>
                <SideItem
                  section={s}
                  active={s.key === current}
                  onClick={() => select(s.key)}
                  variant="row"
                />
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      {/* Conteúdo — `key` força remontar a subárvore ao trocar de seção,
          evitando que o React reaproveite os <input> (campos uncontrolled do
          RHF "vazavam" valor de uma seção para a outra, ex.: RG → Complemento). */}
      <div className="min-w-0" key={active?.key}>
        {active ? active.content : null}
      </div>
    </div>
  )
}

function SideItem({
  section, active, onClick, variant,
}: {
  section: FormSection
  active: boolean
  onClick: () => void
  variant: 'row' | 'chip'
}) {
  const { label, icon: Icon, hasError, errorCount, count } = section
  const countBadge = typeof count === 'number' && (
    <span className={cn('shrink-0 text-xs tabular-nums', active ? 'text-brand-700' : 'text-gray-400')}>{count}</span>
  )
  const errorBadge = hasError && (
    <span className="inline-flex items-center gap-1 shrink-0">
      <AlertCircle size={13} className="text-rose-500" />
      {typeof errorCount === 'number' && errorCount > 0 && (
        <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold">
          {errorCount}
        </span>
      )}
    </span>
  )

  if (variant === 'chip') {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'cursor-pointer inline-flex items-center gap-2 px-3 h-9 rounded-md text-[13px] font-medium transition whitespace-nowrap',
          active
            ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-200'
            : hasError
              ? 'bg-rose-50 text-rose-700 ring-1 ring-rose-200'
              : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50',
        )}
      >
        {Icon && <Icon size={14} />}
        {label}
        {countBadge}
        {errorBadge}
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'cursor-pointer w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] text-left transition relative',
        active
          ? 'bg-brand-50 text-brand-700 font-semibold'
          : 'text-gray-600 hover:bg-gray-50',
      )}
    >
      {active && <span aria-hidden className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r bg-brand-500" />}
      {Icon && <Icon size={15} className={cn('shrink-0', active ? 'text-brand-600' : 'text-gray-400')} />}
      <span className="flex-1 leading-tight">{label}</span>
      {countBadge}
      {errorBadge}
    </button>
  )
}
